/**
 * useWechatShareInit.ts
 *
 * A custom React hook for initializing and managing WeChat share functionality.
 * This hook handles:
 * - WeChat JSSDK initialization for sharing
 * - Automatic share data updates when content changes
 * - URL handling following WeChat JS-SDK documentation
 *
 * Prerequisites:
 * - WeChat public account configured
 * - WeChat JSSDK Provider configured (WechatJSSDKProvider)
 * - wechat-jssdk package installed
 *
 * @see https://developers.weixin.qq.com/doc/service/en/guide/h5/jssdk.html for official documentation
 */

import { DependencyList, useEffect } from 'react'
import { Platform } from 'react-native'
import { isWeChatBrowser } from '../utils/browser'
import * as Sentry from '@sentry/react-native'
import { useWechatJSSDK } from '../contexts/wechat-jssdk.provider'
import type { WechatJSSDKInstance } from './wechat-jssdk-initialize.hook'

const WECHAT_CONSTANTS = {
  DEFAULT_SHARE_IMAGE: 'https://app.meldd.cn/static/media/meldd.jpg',
  API_LIST: ['updateAppMessageShareData'],
}

export type WechatShareData = {
  title: string
  description?: string
}

/**
 * Interface for the return value of updateWechatShareData
 */
interface WechatShareUpdateResult {
  /** Whether the share data was successfully updated */
  success: boolean
}

/**
 * Updates WeChat share data using the JSSDK.
 * This function calls the WeChat updateAppMessageShareData API to customize
 * the share content that appears when users share the page.
 *
 * According to WeChat JS-SDK documentation, the URL must be the full URL
 * without the hash fragment for proper signature verification.
 *
 * @param params - Share update parameters
 * @param params.wxInstance - The initialized WeChat JSSDK instance
 * @param params.shareData - The share data (title and description)
 * @param params.url - The current page URL (without hash fragment)
 * @returns Promise resolving to the update result
 *
 * @see https://developers.weixin.qq.com/doc/service/en/guide/h5/jssdk.html
 */
export async function updateWechatShareData(params: {
  wxInstance: WechatJSSDKInstance
  shareData: WechatShareData
  url: string
}): Promise<WechatShareUpdateResult> {
  const { wxInstance, shareData, url } = params
  const { title, description } = shareData

  if (!title || !url || !wxInstance) {
    return { success: false }
  }

  try {
    return new Promise((resolve) => {
      wxInstance.updateAppMessageShareData({
        title,
        desc: description ?? '',
        link: url,
        imgUrl: WECHAT_CONSTANTS.DEFAULT_SHARE_IMAGE,
        success: () => {
          resolve({ success: true })
        },
        fail: (error: unknown) => {
          const errorMessage =
            typeof error === 'object' && error !== null
              ? JSON.stringify(error)
              : String(error)
          console.error('[updateWechatShareData] Share update failed:', {
            error: errorMessage,
            title,
            url,
          })
          Sentry.captureException(new Error(errorMessage), {
            tags: {
              component: 'WeChatShare',
              operation: 'updateAppMessageShareData',
            },
            extra: {
              title,
              description,
              url,
            },
          })
          resolve({ success: false })
        },
      })
    })
  } catch (err) {
    const errorMessage =
      err instanceof Error ? err.message : 'Unknown share update error'
    console.error('[updateWechatShareData] Share update exception:', {
      error: errorMessage,
      title,
      url,
    })
    Sentry.captureException(err, {
      tags: {
        component: 'WeChatShare',
        operation: 'updateAppMessageShareData',
      },
      extra: {
        title,
        description,
        url,
      },
    })
    return { success: false }
  }
}

/**
 * Gets the WeChat signature URL from the current page.
 * According to WeChat JS-SDK documentation, the URL used for signature
 * must be the full URL without the hash fragment.
 *
 * @returns The current page URL without hash fragment, or null if not on web platform
 *
 * @see https://developers.weixin.qq.com/doc/service/en/guide/h5/jssdk.html
 */
export function getWechatSignatureURL(): string | null {
  if (Platform.OS !== 'web' || !isWeChatBrowser()) {
    return null
  }

  if (typeof location === 'undefined') {
    return null
  }

  return location.href.split('#')[0]
}

/**
 * A hook to initialize WeChat sharing and automatically update share data.
 * This hook handles the complete flow:
 * 1. Gets the current page URL (without hash fragment)
 * 2. Initializes WeChat JSSDK if not already initialized (via shared context)
 * 3. Updates share data when content changes
 *
 * @param getShareData - Function that returns the current share data, or null if not available
 * @param dependencies - Additional dependencies that should trigger share data updates
 *
 * @example
 * // In your component:
 * function TaskDetail({ taskId }: { taskId: string }) {
 *   const { data } = useGetTaskQuery({ variables: { taskId } });
 *   const task = data?.getTask;
 *
 *   useWechatShareInit(() => {
 *     if (!task) return null;
 *     return {
 *       title: task.title,
 *       description: `Task type: ${task.type}`,
 *     };
 *   }, [task]);
 *
 *   return <TaskView task={task} />;
 * }
 */
export const useWechatShareInit = (
  getShareData: () => WechatShareData | null,
  dependencies: DependencyList = []
) => {
  const { wxInstance, isReady, initialize, error } = useWechatJSSDK()
  const wechatSignatureURL = getWechatSignatureURL()

  useEffect(() => {
    if (!wechatSignatureURL) {
      return
    }

    const initAndUpdateShare = async () => {
      if (error && !isReady) {
        console.error(
          '[useWechatShareInit] Skipping share update due to initialization error:',
          error.message
        )
        return
      }

      let instance = wxInstance

      if (!isReady) {
        instance = await initialize(
          wechatSignatureURL,
          WECHAT_CONSTANTS.API_LIST
        )
        if (!instance) {
          if (error) {
            console.error(
              '[useWechatShareInit] Initialization failed:',
              error.message
            )
          }
          return
        }
      }

      if (instance) {
        const shareData = getShareData()
        if (shareData) {
          await updateWechatShareData({
            wxInstance: instance,
            shareData,
            url: wechatSignatureURL,
          })
        }
      }
    }

    void initAndUpdateShare()
  }, [
    isReady,
    wxInstance,
    wechatSignatureURL,
    initialize,
    getShareData,
    error,
    ...dependencies,
  ])
}
